<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAlert } from 'dashboard/composables';
import PortfolioAPI from 'dashboard/api/aceleres/portfolio';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { isTeamNumberContact } from 'dashboard/helper/teamNumber';
import { useContactDetailI18n } from './useContactDetailI18n';

const props = defineProps({ contact: { type: Object, required: true } });
const store = useStore();
const { checkPermissions } = usePolicy();
const { t } = useContactDetailI18n();
const owners = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const pickerVersion = ref(0);
const canAssign = ref(checkPermissions(['administrator']));
const ownerId = computed(() => props.contact.portfolioOwner?.id || '');
const options = computed(() => {
  const rows = owners.value.map(row => ({
    value: row.owner_id,
    label: row.owner_name,
  }));
  if (ownerId.value && !rows.some(row => row.value === ownerId.value)) {
    rows.unshift({
      value: ownerId.value,
      label: props.contact.portfolioOwner.name,
    });
  }
  return rows;
});

onMounted(async () => {
  if (!canAssign.value || isTeamNumberContact(props.contact)) return;
  isLoading.value = true;
  try {
    const { data } = await PortfolioAPI.index();
    owners.value = data.payload.carteiras.filter(row => row.owner_role);
  } catch (error) {
    if (error.response?.status === 403) canAssign.value = false;
    else useAlert(t('CONTACT_DETAIL.OWNER_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

const assignOwner = async value => {
  // Re-selecting a ComboBox option clears it. Keep the current owner instead
  // of releasing the explicit portfolio and falling back to an inferred agent.
  if (!value || value === ownerId.value) {
    pickerVersion.value += 1;
    return;
  }
  isSaving.value = true;
  try {
    const { data } = await PortfolioAPI.assign({
      contactIds: [props.contact.id],
      targetOwnerId: value,
    });
    await store.dispatch('contacts/show', { id: props.contact.id });
    useAlert(
      t(
        data.payload.applied
          ? 'CONTACT_DETAIL.OWNER_SUCCESS'
          : 'CONTACT_DETAIL.OWNER_SKIPPED'
      )
    );
  } catch {
    useAlert(t('CONTACT_DETAIL.OWNER_ERROR'));
  } finally {
    isSaving.value = false;
    pickerVersion.value += 1;
  }
};
</script>

<template>
  <div
    class="flex min-w-0 flex-col gap-1"
    data-testid="contact-owner-assignment"
  >
    <span class="text-xs font-medium text-n-slate-11">{{
      t('CONTACT_DETAIL.OWNER')
    }}</span>
    <ComboBox
      v-if="canAssign && !isTeamNumberContact(contact)"
      :key="`${ownerId}-${pickerVersion}`"
      :model-value="ownerId"
      :options="options"
      :placeholder="
        contact.portfolioOwner?.name || t('CONTACT_DETAIL.NO_OWNER')
      "
      :disabled="isLoading || isSaving"
      class="[&>div>button]:!h-10 [&>div>button]:!rounded-lg [&>div>button]:!bg-white [&>div>button]:!text-sm"
      @update:model-value="assignOwner"
    />
    <span
      v-else
      class="flex h-10 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm text-n-slate-12"
    >
      <span class="i-ph-user-circle size-4 text-n-slate-10" />
      {{ contact.portfolioOwner?.name || t('CONTACT_DETAIL.NO_OWNER') }}
    </span>
  </div>
</template>
